import * as t from "io-ts"

import { BasicFormSpec } from "../../singleApplication/getSchemaTypes"

export const GT_368_FORM_CLASS = "CX_GT_3.68"
export const LT_368_FORM_CLASS = "CX_LT_3.68"

export type CxFormClasses = typeof GT_368_FORM_CLASS | typeof LT_368_FORM_CLASS

// ******************************************************************
// Generic types
// ******************************************************************/

const anyFormEntry = t.union([
    t.string,
    t.number,
    t.boolean,
    t.null,
    t.undefined,
])

const formDataForForm = t.record(t.string, anyFormEntry)

// ******************************************************************
// Raw input types - greater than 3.68
// ******************************************************************/

const CxGt368FormDataRaw = t.strict({
    siteData: formDataForForm,
    files: t.array(t.strict({ fileUrl: t.string, name: t.string })),
    devices: t.array(
        t.intersection([
            t.strict({ deviceSysRef: t.string }),
            t.record(t.string, anyFormEntry),
        ])
    ),
})

export const RawLaunchCxResGt368 = t.type({
    cxFormClass: t.literal("CX_GT_3.68"),
    cxFormVersion: t.number,
    devicesRequiringCx: t.array(t.string),
    formData: t.union([CxGt368FormDataRaw, t.null]),
    formSchema: t.type({
        properties: t.type({
            siteData: BasicFormSpec,
            files: t.any,
            devices: t.type({
                items: BasicFormSpec,
            }),
        }),
    }),
})

export type IRawLaunchCxResGt368 = t.TypeOf<typeof RawLaunchCxResGt368>

// ******************************************************************
// Post parsing types - greater than 3.68
// ******************************************************************/

const CxGt368FormDataParsed = t.intersection([
    t.strict({
        siteData: formDataForForm,
        files: t.array(t.strict({ fileUrl: t.string, name: t.string })),
    }),
    t.record(t.string, formDataForForm),
])

const ParsedLaunchCxResGt368 = t.type({
    cxFormClass: t.literal("CX_GT_3.68"),
    cxFormVersion: t.number,
    devicesRequiringCx: t.array(t.string),
    formData: t.union([CxGt368FormDataParsed, t.null]),
    formSchema: t.type({
        properties: t.type({
            siteData: BasicFormSpec,
            files: t.any,
            devices: t.type({
                items: BasicFormSpec,
            }),
        }),
    }),
    customerAddress: t.string,
    dno: t.string,
})

export type ICxGt368FormDataParsed = t.TypeOf<typeof ParsedLaunchCxResGt368>

// ******************************************************************
//  Input types - Less than 3.68
// ******************************************************************/

const CxLess368FormDataRaw = t.strict({
    siteData: formDataForForm,
    files: t.array(t.strict({ fileUrl: t.string, name: t.string })),
})

export const LaunchCxResLess368 = t.type({
    cxFormClass: t.literal("CX_LT_3.68"),
    cxFormVersion: t.number,
    devicesRequiringCx: t.null,
    formData: t.union([CxLess368FormDataRaw, t.null]),
    formSchema: t.type({
        properties: t.type({
            siteData: BasicFormSpec,
            files: t.any,
        }),
    }),
    customerAddress: t.string,
    dno: t.string,
})

export type ILaunchCxResLess368 = t.TypeOf<typeof LaunchCxResLess368>

// ******************************************************************
// Combined types
// ******************************************************************/

export const RawLaunchCxRes = t.union([RawLaunchCxResGt368, LaunchCxResLess368])
export type IRawLaunchCxRes = IRawLaunchCxResGt368 | ILaunchCxResLess368

export const ParsedLaunchCxRes = t.union([
    ParsedLaunchCxResGt368,
    LaunchCxResLess368,
])

export type IPostParsedLaunchCxRes =
    | ICxGt368FormDataParsed
    | ILaunchCxResLess368

// Type guard for IPostParsedLaunchCxRes
export const isCxGreater368 = (
    data: IPostParsedLaunchCxRes
): data is ICxGt368FormDataParsed =>
    (data as ICxGt368FormDataParsed).cxFormClass === GT_368_FORM_CLASS
